import React, { useMemo, useState, useEffect } from "react";
import { X, FolderTree, Loader2, Home, ChevronRight, ChevronDown, Folder } from "lucide-react";

export interface MoveFolderTreeNode {
  folderId: string;
  name: string;
  children?: MoveFolderTreeNode[];
}

interface MoveFolderModalProps {
  isOpen: boolean;
  sourceFolderId: string;
  sourceFolderName: string;
  currentParentFolderId: string | null;
  rootLabel: string;
  tree: MoveFolderTreeNode[];
  onClose: () => void;
  onSubmit: (newParentFolderId: string | null) => Promise<void> | void;
  isLoading?: boolean;
  isLoadingTree?: boolean;
  externalError?: string | null;
}

const collectDescendantIds = (node: MoveFolderTreeNode): Set<string> => {
  const ids = new Set<string>();
  const walk = (n: MoveFolderTreeNode) => {
    ids.add(n.folderId);
    n.children?.forEach(walk);
  };
  walk(node);
  return ids;
};

const findNode = (
  nodes: MoveFolderTreeNode[],
  folderId: string
): MoveFolderTreeNode | null => {
  for (const node of nodes) {
    if (node.folderId === folderId) return node;
    if (node.children) {
      const found = findNode(node.children, folderId);
      if (found) return found;
    }
  }
  return null;
};

const MoveFolderModal: React.FC<MoveFolderModalProps> = ({
  isOpen,
  sourceFolderId,
  sourceFolderName,
  currentParentFolderId,
  rootLabel,
  tree,
  onClose,
  onSubmit,
  isLoading = false,
  isLoadingTree = false,
  externalError = null,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      setSelectedId(currentParentFolderId);
      setExpanded(new Set());
    }
  }, [isOpen]);

  const disabledIds = useMemo(() => {
    const sourceNode = findNode(tree, sourceFolderId);
    if (!sourceNode) return new Set<string>([sourceFolderId]);
    return collectDescendantIds(sourceNode);
  }, [tree, sourceFolderId]);

  if (!isOpen) return null;

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isRootSelected = selectedId === null;
  const rootIsCurrent = currentParentFolderId === null;
  const targetIsCurrent =
    selectedId !== null && selectedId === currentParentFolderId;
  const submitDisabled = isLoading || targetIsCurrent || (isRootSelected && rootIsCurrent);

  const handleSubmit = async () => {
    if (submitDisabled) return;
    await onSubmit(selectedId);
  };

  const renderNode = (node: MoveFolderTreeNode, depth: number) => {
    const isDisabled = disabledIds.has(node.folderId);
    const isSelected = selectedId === node.folderId;
    const hasChildren = !!node.children && node.children.length > 0;
    const isExpanded = expanded.has(node.folderId);

    return (
      <div key={node.folderId}>
        <button
          type="button"
          disabled={isDisabled}
          onClick={() => !isDisabled && setSelectedId(node.folderId)}
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors text-left
            ${isSelected ? "bg-blue-50 ring-1 ring-blue-300" : "hover:bg-gray-50"}
            ${isDisabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
          style={{ paddingLeft: 8 + depth * 16 }}
        >
          {hasChildren ? (
            <span
              role="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(node.folderId);
              }}
              className="p-0.5 hover:bg-gray-200 rounded"
            >
              {isExpanded ? (
                <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5 text-gray-500" />
              )}
            </span>
          ) : (
            <span className="w-4" />
          )}
          <Folder className="w-4 h-4 text-yellow-500 flex-shrink-0" />
          <span className="truncate text-gray-800">{node.name}</span>
          {isDisabled && node.folderId === sourceFolderId && (
            <span className="ml-auto text-[10px] uppercase tracking-wide text-gray-400">
              this folder
            </span>
          )}
        </button>
        {hasChildren && isExpanded && (
          <div>{node.children!.map((c) => renderNode(c, depth + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="relative w-full max-w-lg animate-in zoom-in-95 duration-200">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="pt-10 p-6">
            <div className="flex items-center justify-between pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <FolderTree className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Move Folder
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">
                    Moving <strong>"{sourceFolderName}"</strong>
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                type="button"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto p-2 bg-gray-50">
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors text-left
                  ${isRootSelected ? "bg-blue-50 ring-1 ring-blue-300" : "hover:bg-gray-100"}`}
              >
                <Home className="w-4 h-4 text-gray-600" />
                <span className="text-gray-800 font-medium">{rootLabel}</span>
                <span className="ml-1 text-[10px] uppercase tracking-wide text-gray-400">
                  root
                </span>
              </button>
              <div className="mt-1">
                {isLoadingTree ? (
                  <div className="flex items-center justify-center py-4 text-gray-400 text-xs">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Loading folders...
                  </div>
                ) : (
                  <>
                    {tree.map((node) => renderNode(node, 0))}
                    {tree.length === 0 && (
                      <p className="text-xs text-gray-400 text-center py-4">
                        No subfolders available
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {externalError && (
              <p className="text-red-500 text-sm mt-3">{externalError}</p>
            )}
            {targetIsCurrent && (
              <p className="text-gray-500 text-xs mt-3">
                Folder is already in this location.
              </p>
            )}

            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="px-6 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitDisabled}
                className="px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm flex items-center gap-2"
                type="button"
              >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Move Here
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoveFolderModal;
